import { ReactNode, useEffect, useMemo } from 'react'
import { OptionsHelper, useDebounce } from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { setTiposPlantaoFromApi } from '../../hooks/tiposPlantaoStore'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: "Baixar agenda"). */
  actions?: ReactNode
}

export const PegaPlantaoSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()

  const debouncedFilters = useDebounce(filters, 500)

  // O período (início/término) vem do calendário; aqui só os filtros da barra.
  useEffect(() => {
    updateState({
      local: debouncedFilters.local?.value || undefined,
      setor: debouncedFilters.setor?.value || undefined,
      tipoPlantao: debouncedFilters.tipoPlantao?.value || undefined,
    })
  }, [debouncedFilters, updateState])

  const setorParams = useMemo(() => ({ localId: filters.local?.value }), [filters.local?.value])

  // Setor pertence ao local: trocar (ou limpar) o local limpa o setor.
  const trocarLocal = (local?: IOption<string>) => {
    setFilter('local', local ?? undefined)
    setFilter('setor', undefined)
  }

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo (o local leva o setor junto).
  const activeFilters: ActiveFilter[] = [
    filters.local && {
      key: 'local',
      label: t('empresa.label'),
      value: filters.local.label,
      onRemove: () => trocarLocal(undefined),
    },
    filters.setor && {
      key: 'setor',
      label: 'Unidade/Setor',
      value: filters.setor.label,
      onRemove: () => setFilter('setor', undefined),
    },
    filters.tipoPlantao && {
      key: 'tipoPlantao',
      label: 'Especialidade',
      value: filters.tipoPlantao.label,
      onRemove: () => setFilter('tipoPlantao', undefined),
    },
  ].filter((filtro): filtro is ActiveFilter => Boolean(filtro))

  return (
    <TableFilters
      actions={actions}
      footer={<ActiveFiltersBar filters={activeFilters} onClearAll={clearFilters} />}
    >
      <TableFilter label={t('empresa.label')} wide>
        <CustomSelect
          entity='empresa'
          onChange={(local?: IOption<string>) => trocarLocal(local)}
          fetchOptions={OptionsHelper.getOptionsLocaisPlantoes}
          value={filters.local}
          autoSelectSingle
        />
      </TableFilter>
      <TableFilter label='Unidade/Setor'>
        <CustomSelect
          placeholder={
            filters.local
              ? 'Selecione uma unidade/setor'
              : `Selecione a ${t('empresa.label.lowercase')}`
          }
          fetchOptions={OptionsHelper.getOptionsSetoresPlantoes}
          fetchOptionsParams={setorParams}
          onChange={(setor?: IOption<string>) => setFilter('setor', setor ?? undefined)}
          value={filters.setor}
          isDisabled={!filters.local}
        />
      </TableFilter>
      <TableFilter label='Especialidade'>
        <CustomSelect
          placeholder='Selecione uma especialidade'
          onChange={(tipoPlantao?: IOption<string>) =>
            setFilter('tipoPlantao', tipoPlantao ?? undefined)
          }
          fetchOptions={OptionsHelper.getOptionsTiposPlantao}
          value={filters.tipoPlantao}
          onOptionsLoaded={(rawData) => setTiposPlantaoFromApi(rawData)}
        />
      </TableFilter>
    </TableFilters>
  )
}
